import { Link } from 'react-router-dom';
import type { Article } from '@/mocks/articles';

function formatViews(views: number): string {
  if (views >= 10000) {
    return (views / 10000).toFixed(1) + '万';
  }
  return views.toString();
}

interface ArticleCardProps {
  article: Article;
  variant?: 'vertical' | 'horizontal';
}

export default function ArticleCard({ article, variant = 'vertical' }: ArticleCardProps) {
  if (variant === 'horizontal') {
    return (
      <Link
        to={`/article/${article.slug}`}
        className="group flex gap-4 p-3 rounded-lg bg-background-50 hover:bg-background-100 transition-colors cursor-pointer"
      >
        <div className="w-28 h-20 flex-shrink-0 rounded-lg overflow-hidden">
          <img
            src={article.imageUrl}
            alt={article.title}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
            loading="lazy"
          />
        </div>
        <div className="flex flex-col justify-between min-w-0 flex-1">
          <h4 className="text-sm font-medium text-foreground-900 line-clamp-2 group-hover:text-primary-500 transition-colors">
            {article.title}
          </h4>
          <div className="flex items-center gap-3 text-xs text-foreground-500 mt-1">
            <span>{article.date}</span>
            <span className="flex items-center gap-1">
              <i className="ri-eye-line"></i>
              {formatViews(article.views)}
            </span>
          </div>
        </div>
      </Link>
    );
  }

  return (
    <Link
      to={`/article/${article.slug}`}
      className="group block rounded-lg overflow-hidden bg-background-50 hover:bg-background-100 transition-all duration-300 cursor-pointer"
    >
      <div className="relative aspect-[16/10] overflow-hidden">
        <img
          src={article.imageUrl}
          alt={article.title}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
          loading="lazy"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent"></div>
        <div className="absolute top-3 left-3 flex gap-2">
          {article.isHot && (
            <span className="text-xs bg-primary-500 text-white px-2 py-0.5 rounded-full whitespace-nowrap">
              热门
            </span>
          )}
          {article.isNew && (
            <span className="text-xs bg-accent-500 text-background-50 px-2 py-0.5 rounded-full whitespace-nowrap">
              新瓜
            </span>
          )}
        </div>
        <span className="absolute top-3 right-3 text-xs bg-black/50 text-white px-2 py-0.5 rounded-full whitespace-nowrap">
          {article.category}
        </span>
      </div>
      <div className="p-4">
        <h3 className="text-sm font-medium text-foreground-900 line-clamp-2 group-hover:text-primary-500 transition-colors leading-snug">
          {article.title}
        </h3>
        <p className="text-xs text-foreground-500 mt-2 line-clamp-2 leading-relaxed">
          {article.excerpt}
        </p>
        <div className="flex items-center gap-3 mt-3 text-xs text-foreground-500">
          <span className="flex items-center gap-1">
            <i className="ri-eye-line"></i>
            {formatViews(article.views)}
          </span>
          <span className="flex items-center gap-1">
            <i className="ri-chat-3-line"></i>
            {formatViews(article.commentCount)}
          </span>
          <span className="ml-auto">{article.date}</span>
        </div>
      </div>
    </Link>
  );
}